import { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  /* Disable text cursor and selection on ALL elements by default */
  * {
    cursor: default !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
    caret-color: transparent !important;
  }

  /* Only show text cursor when input is focused and active */
  input:focus,
  textarea:focus,
  [contenteditable="true"]:focus,
  [contenteditable=""]:focus {
    cursor: text !important;
    caret-color: auto !important;
    -webkit-user-select: text !important;
    -moz-user-select: text !important;
    -ms-user-select: text !important;
    user-select: text !important;
  }

  /* Hide caret when input is not focused */
  input:not(:focus),
  textarea:not(:focus),
  [contenteditable="true"]:not(:focus),
  [contenteditable=""]:not(:focus) {
    caret-color: transparent !important;
  }

  /* Allow pointer cursor on interactive elements */
  button,
  a,
  [role="button"],
  [onclick],
  [tabindex]:not([tabindex="-1"]) {
    cursor: pointer !important;
  }

  /* Allow text selection on text content areas (paragraphs, spans, etc.) */
  p,
  span,
  h1, h2, h3, h4, h5, h6,
  li,
  td,
  th,
  label {
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
    user-select: text;
    cursor: text;
  }

  /* Allow text selection on specific content containers */
  div[class*="message"],
  div[class*="content"],
  div[class*="text"],
  div[class*="chat"],
  div[class*="Message"] {
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
    user-select: text;
  }

  html {
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    scroll-padding-top: 5rem; /* Account for fixed header */
  }

  html, body {
    height: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain; /* Prevent scroll chaining */
    overscroll-behavior-x: none;
    -webkit-text-size-adjust: 100%;
    -webkit-overflow-scrolling: touch; /* Smooth momentum scrolling on iOS */
  }

  body {
    font-family: var(--font-family-sans);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    background: var(--color-background);
    color: var(--color-text-primary);
    transition: background-color var(--transition-color-fast), color var(--transition-color-fast);
  }

#root {
    min-height: 100%;
    width: 100vw;
    max-width: 100vw;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-x: hidden;
  }

  /* Modern smooth scrolling for all scrollable elements */
  * {
    scroll-behavior: smooth;
    scrollbar-width: thin;
    scrollbar-color: rgba(204, 51, 51, 0.5) rgba(255, 255, 255, 0.05);
  }

  @media (prefers-reduced-motion: reduce) {
    * {
      scroll-behavior: auto;
    }
  }

  /* Custom scrollbar styles - modern, minimal design */
  ::-webkit-scrollbar {
    width: 0.5rem; /* 8px in rem - slightly wider for better visibility */
    height: 0.5rem;
    -webkit-appearance: none;
  }

  /* Hide scrollbar arrows completely */
  ::-webkit-scrollbar-button {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    -webkit-appearance: none;
  }

  ::-webkit-scrollbar-button:start:decrement,
  ::-webkit-scrollbar-button:end:increment {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  ::-webkit-scrollbar-button:vertical:start:decrement,
  ::-webkit-scrollbar-button:vertical:end:increment,
  ::-webkit-scrollbar-button:horizontal:start:decrement,
  ::-webkit-scrollbar-button:horizontal:end:increment {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }

  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: var(--spacing-1);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(204, 51, 51, 0.5);
    border-radius: 999px; /* Pill shape - fully rounded ends */
    transition: background 0.2s ease, width 0.2s ease;
    min-height: 2.5rem; /* 40px in rem */
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(204, 51, 51, 0.8);
    width: 0.625rem; /* Slightly wider on hover */
  }

  /* Animations */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0%, 100% { 
      opacity: 0.6; 
    }
    50% { 
      opacity: 1; 
    }
  }

  /* Focus styles for accessibility - WCAG AA compliant */
  button:focus-visible,
  input:focus-visible,
  textarea:focus-visible,
  select:focus-visible,
  a:focus-visible,
  [role="button"]:focus-visible {
    outline: 0.125rem solid var(--color-accent);
    outline-offset: 0.125rem;
    box-shadow: 0 0 0 0.25rem rgba(var(--color-accent-rgb), 0.2);
  }
  
  /* Remove default focus outline for mouse users, keep for keyboard */
  button:focus:not(:focus-visible),
  input:focus:not(:focus-visible),
  textarea:focus:not(:focus-visible),
  select:focus:not(:focus-visible),
  a:focus:not(:focus-visible) {
    outline: none;
  }
  
  /* Ensure focus is visible for keyboard navigation */
  *:focus-visible {
    outline: 0.125rem solid var(--color-accent);
    outline-offset: 0.125rem;
  }

  /* Smooth transitions - all color changes happen simultaneously */
  * {
    transition: background-color var(--transition-color-fast), border-color var(--transition-color-fast), color var(--transition-color-fast);
  }
  
  /* Mobile-specific improvements */
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    /* Reduce motion for better performance on mobile */
    @media (prefers-reduced-motion: reduce) {
      * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
    
    /* Better touch targets */
    button, input, textarea, select, a {
      min-height: 2.75rem; /* 44px in rem - accessibility standard */
      min-width: 2.75rem; /* 44px in rem - accessibility standard */
    }
    
    /* Prevent text selection on UI elements */
    button, .no-select {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
    }
  }
  
  /* Enhanced mobile-specific improvements */
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    /* Larger touch targets on small screens */
    button, input[type="button"], input[type="submit"], a[role="button"] {
      min-height: 3rem; /* 48px - larger for easier tapping */
      min-width: 3rem;
      padding: var(--spacing-3) var(--spacing-4);
    }
    
    /* Prevent iOS zoom on input focus */
    input, textarea, select {
      font-size: 16px !important; /* Prevents iOS zoom */
    }
    
    /* Improve scrolling on mobile */
    * {
      -webkit-overflow-scrolling: touch;
    }
  }
  
  /* Landscape orientation adjustments for mobile */
  @media screen and (orientation: landscape) and (max-width: 48rem) {
    /* Reduce padding in landscape mode */
    body {
      font-size: var(--font-size-sm);
    }
  }
  
  /* Landscape orientation adjustments */
  @media screen and (orientation: landscape) and (max-height: 31.25rem) {
    body {
      font-size: var(--font-size-sm); /* 0.875rem */
    }
  }

  /* Suppress ResizeObserver errors */
  .resize-observer-error {
    display: none !important;
  }
  
  /* Screen reader only content */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
  
  /* Skip link styles */
  .skip-link {
    position: absolute;
    top: -3.5rem;
    left: var(--spacing-4);
    z-index: 10000;
    padding: var(--spacing-3) var(--spacing-5);
    background: var(--color-accent);
    color: #ffffff;
    text-decoration: none;
    font-weight: 600;
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-soft);
    transition: top 0.2s ease;
  }
  
  .skip-link:focus {
    top: var(--spacing-4);
    outline: 0.125rem solid var(--color-accent-light);
    outline-offset: 0.125rem;
  }
  
  /* High contrast mode support */
  @media (prefers-contrast: high) {
    * {
      border-color: currentColor !important;
    }
    
    button, a {
      border: 0.125rem solid currentColor !important;
    }
  }
  
  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`; 